// React
import { useCallback, useEffect, useState } from "react";

// Servisni sloj
import { getEffectivePricingVersionAPI } from "services/api/pricing/api";
import {
  getPricingSchemeImpactAPI,
  getPricingSchemesAPI,
} from "services/api/pricing/schemeApi";
import { PricingScheme, PricingSchemeImpact } from "services/api/pricing/schemeTypes";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { PRICING_SCHEMES_PAGE_TITLE } from "ui/components/PageTitle/consts";

// Moduli specifični za projekt
import { Routes } from "ui/modules/navigation";
import PricingTabMenu from "ui/modules/pricing/components/PricingTabMenu/PricingTabMenu";

import PricingSchemeEditCard from "ui/modules/pricing/components/PricingSchemeEditCard/PricingSchemeEditCard";

// Lokalne komponente
import PricingSchemeCard from "./components/PricingSchemeCard/PricingSchemeCard";
import PricingSchemeImpactCard from "./components/PricingSchemeImpactCard/PricingSchemeImpactCard";

// Tipovi
import { PricingSchemesPageCmp, PricingSchemesPageProps } from "./types";

/**
 * Cjenovne sheme — sloj koji sprječava da svaki pregovor završi kao pojedinačni zapis.
 */
const PricingSchemesPage: PricingSchemesPageCmp = (_props: PricingSchemesPageProps) => {
  const title = PRICING_SCHEMES_PAGE_TITLE;

  const [schemes, setSchemes] = useState<PricingScheme[]>([]);
  const [overridableKeys, setOverridableKeys] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  /** Učinak po grupi, po `id`. Stoji uz svaku tablicu, pa se dohvaća za sve odjednom. */
  const [impacts, setImpacts] = useState<Record<string, PricingSchemeImpact>>({});

  const [editScheme, setEditScheme] = useState<PricingScheme | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const [impactScheme, setImpactScheme] = useState<PricingScheme | null>(null);
  const [impact, setImpact] = useState<PricingSchemeImpact | null>(null);

  useEffect(() => {
    fetchInitial();
  }, []);

  const fetchInitial = async () => {
    setIsLoading(true);

    try {
      const [all, version] = await Promise.all([
        getPricingSchemesAPI(),
        getEffectivePricingVersionAPI(),
      ]);

      setSchemes(all);

      // Shema smije gađati kombinacije packova i ostale vrijednosti — nabavne cijene ne.
      setOverridableKeys([
        ...version.combinations.map((combination) => combination.keyCode),
        ...version.otherValues.map((value) => value.code),
        ...version.meteredItems.map((item) => item.code),
      ]);

      await fetchImpacts(all);
    } catch {
      // greška se prikazuje kroz snackbar
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Učinak se računa po grupi, pa ide zahtjev po grupi. Idu usporedno, a grupa čiji dohvat
   * padne jednostavno ostaje bez brojke; popis se zbog toga ne ruši.
   */
  const fetchImpacts = async (all: PricingScheme[]) => {
    const results = await Promise.all(
      all.map(async (scheme) => {
        try {
          return [scheme.id, await getPricingSchemeImpactAPI(scheme.id)] as const;
        } catch {
          return null;
        }
      })
    );

    setImpacts(Object.fromEntries(results.filter(Boolean) as [string, PricingSchemeImpact][]));
  };

  const handleAddClick = useCallback(() => {
    setEditScheme(null);
    setShowEditModal(true);
  }, []);

  const handleEdit = useCallback((scheme: PricingScheme) => {
    setEditScheme(scheme);
    setShowEditModal(true);
  }, []);

  const handleEditClose = useCallback(() => {
    setShowEditModal(false);
    setEditScheme(null);
  }, []);

  const handleEditSuccess = useCallback(async () => {
    setShowEditModal(false);
    setEditScheme(null);

    try {
      const all = await getPricingSchemesAPI();

      setSchemes(all);

      // Izmjena stavaka mijenja i učinak, pa se brojke uz tablice moraju osvježiti.
      await fetchImpacts(all);
    } catch {
      // greška se prikazuje kroz snackbar
    }
  }, []);

  const handleShowImpact = useCallback(async (scheme: PricingScheme) => {
    setImpactScheme(scheme);
    setImpact(null);

    try {
      setImpact(await getPricingSchemeImpactAPI(scheme.id));
    } catch {
      // greška se prikazuje kroz snackbar
    }
  }, []);

  const handleImpactClose = useCallback(() => {
    setImpactScheme(null);
    setImpact(null);
  }, []);

  return (
    <div>
      <div className="bg-white rounded-t-md">
        <PageHeader>
          <div className="flex items-center justify-between">
            <div>
              <PageTitle title={title.text} icon={title.icon} iconColor={title.iconColor} />
              <Breadcrumb />
            </div>

            <Button
              variant="primary"
              children="Nova grupa"
              onClick={handleAddClick}
              startIcon="/media/icons/plus.svg"
            />
          </div>
        </PageHeader>
        <PricingTabMenu />
      </div>

      {/* Jedna ploha, sheme razdvojene crtom — ne niz zasebnih bijelih kartica. */}
      <div className="divide-y divide-slate-200">
        {schemes.map((scheme) => (
          <PricingSchemeCard
            key={scheme.id}
            scheme={scheme}
            impact={impacts[scheme.id] ?? null}
            onEdit={handleEdit}
            onShowImpact={handleShowImpact}
          />
        ))}

        {!isLoading && schemes.length === 0 && (
          <div className="p-6">
            <p className="text-sm text-slate-500">Nijedna grupa još nije napravljena.</p>
          </div>
        )}
      </div>

      <PricingSchemeEditCard
        show={showEditModal}
        scheme={editScheme}
        overridableKeys={overridableKeys}
        onSuccess={handleEditSuccess}
        onClose={handleEditClose}
      />

      <PricingSchemeImpactCard
        scheme={impactScheme}
        impact={impact}
        onClose={handleImpactClose}
      />

      <Loader isLoading={isLoading} message="Učitavanje podataka…">
        {null}
      </Loader>
    </div>
  );
};

PricingSchemesPage.route = Routes.PricingSchemesPageRouter;

export default PricingSchemesPage;
